import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {QuestionBase} from "../../pages/create-adv/models/question-base";
import {ControlValueAccessor, FormControl, ReactiveFormsModule} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {FormatInputDirective} from "../../shared/format-input-directive/format-input-directive";
import {
  cleanValue,
  displayValue,
  InputFormat,
  inputFormatOf,
  inputModeOf,
  isMaskedFormat,
  unitKey
} from "../../shared/field-format";

@Component({
    selector: 'app-text-field',
    templateUrl: './text-field.component.html',
    providers: [FormatInputDirective],
    imports: [
        ReactiveFormsModule,
        TranslateModule
    ],
    styleUrls: ['./text-field.component.scss']
})
export class TextFieldComponent implements OnInit, ControlValueAccessor  {

  @Input() question: QuestionBase<string>
  @Output() onValueChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() formControlName: string;
  @Input() control: FormControl;

  format: InputFormat = 'text';
  masked = false;
  unit: string | null = null;
  inputMode = 'text';
  nativeType = 'text';
  display = '';

  onChange: any = () => {};
  onTouch: any = () => {};

  writeValue(value: any): void {
    this.control.setValue(value);
    this.display = displayValue(this.format, value);
    this.onChange(value);
    this.onTouch(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  /** Plates / phones are short: stop typing at their full formatted length. */
  get maxDisplayLength(): number | null {
    if (this.format === 'licensePlate') return 10;
    if (this.format === 'phone') return 11;
    return null;
  }

  executeParentFunction($event:any) {
    this.onValueChange.emit($event);
  }

  /** Re-format while typing, keeping the caret after the same digit. */
  onFormattedInput(input: HTMLInputElement): void {
    const caret = input.selectionStart ?? input.value.length;
    // Only what the user types counts: digits, the decimal point (decimal)
    // and a leading minus (integer / decimal). The separators the mask adds
    // ("," and the plate / phone "-") must not, or the caret lands before the
    // last digit and the next one goes in the wrong place (12-3 + 4 -> 12-43).
    const counts = (text: string, i: number): boolean => {
      const ch = text[i];
      if (ch >= '0' && ch <= '9') return true;
      if (this.format === 'decimal' && ch === '.') return true;
      return (this.format === 'integer' || this.format === 'decimal') && ch === '-' && text.slice(0, i).trim() === '';
    };
    let digitsBeforeCaret = 0;
    for (let i = 0; i < caret && i < input.value.length; i++) {
      if (counts(input.value, i)) digitsBeforeCaret++;
    }
    const raw = cleanValue(this.format, input.value);
    this.display = displayValue(this.format, raw);
    input.value = this.display;
    let position = 0, seen = 0;
    while (position < this.display.length && seen < digitsBeforeCaret) {
      if (counts(this.display, position)) seen++;
      position++;
    }
    input.setSelectionRange(position, position);
    this.control?.setValue(raw, {emitEvent: false});
    this.control?.markAsDirty();
  }

  /** On leaving the field: hand the PLAIN value to the form (not "1,250,000"). */
  commitFormatted(): void {
    const raw = this.control?.value ?? '';
    this.onValueChange.emit({target: {value: raw}});
  }

  ngOnInit(): void {
    this.format = inputFormatOf(this.question);
    this.masked = isMaskedFormat(this.format);
    this.unit = unitKey(this.format);
    this.inputMode = inputModeOf(this.format);
    this.nativeType = this.question?.type === 'bindSelect' ? 'text'
      : this.format === 'date' ? 'date'
      : this.format === 'email' ? 'email'
      : (this.question?.type || 'text');
    this.display = displayValue(this.format, this.control?.value);
  }

}
